import dayjs from 'dayjs'

export const SCREEN_TITLE = '반품작업_신규'

/**
 * 출고일자 상수(today-30d ~ today). 셸(8.1-6)에는 노출 대상이 아니었으나 사용자가 고를 수 없는
 * 조건이라 범위 밖 스캔이 이유 없는 무결과로 보였다 — `formatSearchScope` 로 안내에 싣는다
 * (LOGISPRD-12613).
 * 호출 시점에 계산한다 — 모듈 로드 시각에 고정하면 자정을 넘긴 세션에서 오늘이 빠진다.
 *
 * 형식은 `yyyyMMdd`(대시 없음) — 1.0 `RGR11.js:154` 의 `ACT_DATE` 가 이 형식으로 나갔고,
 * 같은 반품 도메인의 `returnInbound/apis.ts:17-20` 도 BE 가 대시 있는 ISO 를 주면 400 이 난다고
 * 명시한다(PR #691 리뷰 코멘트, gyoungjun).
 */
export const getActDateRange = (): { actDateFrom: string; actDateTo: string } => {
  const today = dayjs()
  return { actDateFrom: today.subtract(30, 'day').format('YYYYMMDD'), actDateTo: today.format('YYYYMMDD') }
}

/**
 * 통합반품 화주 — 등록센터 노출·`centerNm` 필수 분기 조건(8.1-6).
 *
 * `INTERGRATION` 은 오타처럼 보이지만 1.0 원본 값 그대로다 — `RGR11.js:903`,
 * `FilterBar.js:985` 모두 이 철자로 `sprNm` 을 비교한다(PR #691 리뷰 코멘트로 오타 의심 제기,
 * 1.0 확인 후 유지로 닫음).
 */
export const INTEGRATION_RETURN_SHIPPER_CODE = 'INTERGRATION_RETURN'

/** GBGH 전용 필드(d-ERP 전송여부·수기입력사유) 노출 조건(1.0 `isShipperGBGH`, 소문자 비교). */
export const GBGH_SHIPPER_CODES = ['gbgh', 'budhimudra']

export const isIntegrationReturnShipper = (shipperCode: string): boolean => shipperCode === INTEGRATION_RETURN_SHIPPER_CODE

export const isGbghShipper = (shipperCode: string): boolean => GBGH_SHIPPER_CODES.includes(shipperCode.toLowerCase())

/**
 * 등록센터 옵션 — TODO(spec): 1.0·핸드오프 셸 어디에도 고정 목록이 없다(RETURN_CENTER 공통코드
 * 목록이라는 서술만 3.1 에 있다). 값을 지어내지 않고 자유 입력(Input)으로 구현하며, 실제 목록이
 * 확정되면 `common-codes`(codeGrp=RETURN_CENTER) 조회로 바꾼다. PR 설명에 계획 대비 차이로 남긴다.
 */
export const CENTER_NM_PLACEHOLDER = '등록센터명 입력'

/** `yyyyMMdd` → `yyyy-MM-dd`. 안내 문구 전용 — 요청은 대시 없는 형식 그대로 나간다. */
export const formatActDateLabel = (value: string): string => (/^\d{8}$/.test(value) ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}` : value)

/** 조회 범위 안내의 입력 — 실제 요청 파라미터(`WorkInvoiceQuery`)를 그대로 받는다. */
export interface SearchScope {
  shipper: string
  actDateFrom: string
  actDateTo: string
  centerNm?: string
}

/**
 * 화주·출고일자는 사용자가 고를 수 없이 고정으로 실려 나가는데 화면에 없어서, 다른 화주의
 * 반송장이나 30일 밖 건을 스캔하면 이유를 알 수 없는 무결과로만 보였다(LOGISPRD-12613 —
 * LOGISPRD-12578 dev E2E 1회차). 조회 범위를 운송장 카드와 무결과 안내 양쪽에 같은 문구로 싣는다.
 */
export const formatSearchScope = (scope: SearchScope): string => {
  const parts = [`화주 ${scope.shipper || '-'}`, `출고일자 ${formatActDateLabel(scope.actDateFrom)} ~ ${formatActDateLabel(scope.actDateTo)}`]
  if (scope.centerNm) parts.push(`등록센터 ${scope.centerNm}`)
  return parts.join(' · ')
}

/** 조회 범위 안내 앞에 붙는 라벨 — 카드 표시와 테스트가 같은 값을 본다. */
export const SEARCH_SCOPE_LABEL = '조회 범위'

export const MESSAGES = {
  invoiceRequired: '반송장을 입력해주세요.',
  ordNoRequired: '반품번호를 입력해주세요.',
  invoiceOrOrgInvoiceRequired: '반송장 및 원송장을 입력해주세요.',
  /** 무결과 안내에 무엇으로 걸렀는지 함께 담는다(LOGISPRD-12613). */
  /** 조회 요청 자체가 실패(서버 오류) — 무결과(notFoundInScope)와 구분한다. */
  searchFailed: '조회 중 오류가 발생했습니다.',
  notFoundInScope: (scope: string): string => `조회 결과가 존재하지 않습니다. (${SEARCH_SCOPE_LABEL} ${scope})`,
  partialCallNoCondition: '불러올 송장 정보가 없습니다.',
  attachmentUploadFailed: '검수 영상·이미지 첨부가 실패했습니다. 저장은 완료되었습니다. 다시 시도해 주세요.',
  attachmentTargetMissing: '검수 첨부 대상을 받지 못했습니다. 관리자에게 문의해 주세요.',
  saveDuplicates: (count: number): string => `이미 등록된 반품번호 ${count}건은 저장에서 제외되었습니다.`,
  saveSuccess: '저장되었습니다.',
  saveFailed: '저장 중 오류가 발생했습니다. 다시 시도해 주세요.',
  centerNmRequired: '등록센터를 입력해주세요.',
  orderNotPicked: '주문을 선택해 주세요.',
  orderSearchFailed: '주문검색 중 오류가 발생했습니다.',
  printRequested: '인쇄를 요청했습니다.',
  printFailed: (count: number): string => `${count}건 인쇄에 실패했습니다. qz-tray 설치·연결 상태를 확인해 주세요.`,
  cameraConnecting: '카메라 연결 중',
  cameraUnavailable: '카메라를 사용할 수 없습니다',
} as const

/** 마스터 조회여부 옵션(생성 셸 `MASTERFLAGSELECT_OPTIONS` 그대로). */
export const MASTER_FLAG_OPTIONS: { label: string; value: 'N' | 'Y' }[] = [
  { label: 'N (원송장 주문목록 기준)', value: 'N' },
  { label: 'Y (상품 마스터 기준)', value: 'Y' },
]

/** 정불구분 옵션 — 검수표 편집 셀(`ETC5`). */
export const CONDITION_OPTIONS: { label: string; value: string }[] = [
  { label: '정상', value: '정상' },
  { label: '불량', value: '불량' },
]

/** 1.0 치트 — 상품바코드에 이 값을 스캔하면 저장을 실행한다(화면 미표시, 8.1-17). */
export const SAVE_BARCODE_CHEAT = 'SAVE'

/** d-ERP 전송여부(GBGH 전용) — 뷰 셀렉트 값 Y/N ↔ 저장 값 boolean. */
export const DOOSON_ERP_OPTIONS: { label: string; value: 'Y' | 'N' }[] = [
  { label: 'Y', value: 'Y' },
  { label: 'N', value: 'N' },
]

/** 바코드 출력 라벨지 규격 — 바코드(3×4.5in) 하나. 1.0 재발행은 늘 바코드 규격으로만 나가 '라벨' 을 골라도 같았다(LOGISPRD-12578) — PD 2026-10-02 '라벨' 항목 제거. */
export const SPEC_OPTIONS: { label: string; value: string }[] = [{ label: '바코드 (3 × 4.5in)', value: 'barcode' }]

/** 등록센터 입력을 세션·URL 에 남기기까지 기다리는 시간(ms) — 타이핑마다 세션 쓰기·라우터 replace 가 돌지 않게(옛 blur 커밋 자리, PR #691 리뷰). */
export const CENTER_NM_PERSIST_DELAY_MS = 500
